import { useEffect, useState } from 'react';
import { companyService } from '@root/app/shared/new-services/company.service';

/**
 * Module-level cache so every caller of `useActionsEnabled` reuses the same
 * in-flight fetch / resolved value. Without this each row that renders the
 * action-related "Transform into action" button would fire its own
 * `getConfigKey` call.
 */
let cachedValue: boolean | null = null;
let inflight: Promise<boolean> | null = null;
const subscribers = new Set<(value: boolean) => void>();

const fetchActionsEnabled = (): Promise<boolean> => {
    if (cachedValue !== null) {
        return Promise.resolve(cachedValue);
    }
    if (inflight) {
        return inflight;
    }
    inflight = new Promise<boolean>((resolve) => {
        companyService.getConfigKey('actionsEnabled', (err, value) => {
            const resolved = !err && Boolean(value);
            cachedValue = resolved;
            inflight = null;
            subscribers.forEach((notify) => notify(resolved));
            resolve(resolved);
        });
    });
    return inflight;
};

/**
 * Returns the cached value of the `actionsEnabled` company feature flag.
 * Useful to gate the UI surfaces that depend on the Actions module being
 * active for the company (Action templates, "Transform idea into action",
 * the Actions sidebar entry, etc.).
 *
 * The fetch is deduped across all callers — the first hook instance triggers
 * the request and all subsequent ones receive the resolved value from cache.
 */
const useActionsEnabled = (): boolean => {
    const [enabled, setEnabled] = useState<boolean>(cachedValue ?? false);

    useEffect(() => {
        let active = true;
        if (cachedValue !== null) {
            setEnabled(cachedValue);
            return undefined;
        }

        const notify = (value: boolean) => {
            if (active) { setEnabled(value); }
        };
        subscribers.add(notify);
        fetchActionsEnabled().then((value) => {
            if (active) { setEnabled(value); }
        });
        return () => {
            active = false;
            subscribers.delete(notify);
        };
    }, []);

    return enabled;
};

export default useActionsEnabled;
